import { Injectable, ViewContainerRef } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { DmpServiceAbstract, DmpParameters, HistoriqueVaccination, HistoriqueVaccinationImmunizations, UpdateNoteVaccinationId, VaccinationConsumable, VaccinationVaccinator, VaccinAValider, ValidationVaccination } from '../dmp-service.abstract';
import { EdcAuthService } from '../../auth/edc-auth.service';
import { environment } from 'src/environments/environment';
import { DmpHlGetDirectAuthentificationDmpStatus } from 'src/app/utils/interfaces/new/dmp-hl-get-direct-authentification-dmp-status.interface';
import { DmpHlUpdateUserAccessAuthorizationParams } from 'src/app/utils/interfaces/new/dmp-hl-update-user-access-authorization-identity-params.interface';
import { DmpHlAccessModeEnum, DmpHlSetDmpAccessModeParams } from 'src/app/utils/interfaces/new/dmp-hl-set-dmp-access-mode-params.interface';
import { DmpHlFindPatientResponseSIns } from 'src/app/utils/interfaces/new/dmp-hl-find-patient-response.interface';
import { DmpHlGetWebPsUrlsResponse } from 'src/app/utils/interfaces/new/dmp-hl-get-web-ps-urls-response.interface';
import { DmpHlGetInteropCodesEnum } from 'src/app/utils/enums/dmp-hl-get-interop-codes-params.enum';
import { DmpHlGetInteropCodesResponse } from 'src/app/utils/interfaces/new/dmp-hl-get-interop-codes-response.interface';
import { DmpHlFindDocumentsParams } from 'src/app/utils/interfaces/new/dmp-hl-find-documents-params.interface';
import { DmpHlFindDocumentsResponse } from 'src/app/utils/interfaces/new/dmp-hl-find-documents-response.interface';
import { DmpHlGetDocumentContentParams } from 'src/app/utils/interfaces/new/dmp-hl-get-document-content-params.interface';
import { DmpDocumentPerformerRoleEnum, DmpHlGetDocumentContentResponse } from 'src/app/utils/interfaces/new/dmp-hl-get-document-content-response.interface';
import { DmpHlDeleteDocumentParams } from 'src/app/utils/interfaces/new/dmp-hl-delete-document-params.interface';
import { DmpGetMyDocumentsParams } from 'src/app/utils/interfaces/new/dmp-get-my-documents-params.interface';
import { DmpGetMyDocumentsResult } from 'src/app/utils/interfaces/new/dmp-get-my-documents-result.interface';
import { DmpGetLastAccessDateParams } from 'src/app/utils/interfaces/new/dmp-get-last-access-date-params.interface';
import { DmpHlArchiveDocumentParams } from 'src/app/utils/interfaces/new/dmp-hl-archive-document-params.interface';
import { DmpHlVisibilityDocumentParams } from 'src/app/utils/interfaces/new/dmp-hl-visibility-document-params.interface';
import { DmpHlsendDocumentsDocument, DmpHlsendDocumentsParams } from 'src/app/utils/interfaces/new/dmp-hl-send-documents-params.interface';
import { DmpAddMyDocumentsParams } from 'src/app/utils/interfaces/new/dmp-add-my-documents-params.interface';
import { UserInfos } from '../dmp.service';
import { DmpVaccin } from '../../vaccin/vaccin.service';
import { LoginService } from '../../login/login.service';
import { v4 } from 'uuid';
import moment from 'moment';
import {
  mapRestToAtcCodes,
  mapRestToCisCodes,
  mapRestToDmpParameters,
  mapRestToDmpStatus,
  mapRestToFindDocuments
} from './dmp-rest-mapper';
import { DmpVisibilityEnum } from 'src/app/utils/enums/dmp-visibility.enum';
import { DomainEnum, LogFonctionnelCallerService, LogFonctionnelService, LogObject, ResultEnum } from '../../log-fonctionnel/log-fonctionnel.service';
import { buildDeleteDocLog, buildSendDocErrorLog, buildSendDocSuccessLog } from './dmp-log.utils';
import {
  DmpRestDeleteDocumentBody,
  DmpRestFindDocumentsBody,
  DmpRestGetAtcCodesBody,
  DmpRestGetCisCodesBody,
  DmpRestGetDmpParametersBody,
  DmpRestGetDmpStatusBody,
  DmpRestGetDocumentContentBody,
  DmpRestGetDocumentFromCdaBody,
  DmpRestGetDocumentUuidByUniqueIdBody,
  DmpRestGetInteropCodesBody,
  DmpRestGetMajorityAgeBody,
  DmpRestGetWebPsDmpUrlsBody,
  DmpRestSendDocumentResult,
  DmpRestSendSubmissionSetAuthor,
  DmpRestSendSubmissionSetBody,
  DmpRestSendSubmissionSetPerformer,
  DmpRestSendSubmissionSetResponse,
  DmpRestUpdateDmpAccessAuthorizationBody,
  DmpRestUpdateDocumentStatusBody,
  DmpRestUpdateDocumentVisibilityBody
} from 'src/app/utils/interfaces/new/dmp-rest-payloads.interface';

const DOCUMENT_PATH = '/medical/documents-dmp';
const DMP_LAST_ACCESS_PATH = '/medical/dernier-acces-dmp';
/** OID de la nomenclature ASIP/RPPS "Profession", constant quel que soit le professionnel (cf. vaccin.service.ts, annuaire.component.ts, etc.) */
const DMP_PROFESSION_OID = '1.2.250.1.71.1.2.7';

@Injectable({
  providedIn: 'root'
})
export class DmpRestStrategyService extends DmpServiceAbstract {
  public resourceId: string;
  public accessMode: DmpHlAccessModeEnum;
  private _accessModeReason: string;

  /*
   * Cette URL pointe en réalité vers une Cloud Function
   * qui se charge de faire le lien avec le proxy iCanopée.
   * Cette Cloud Function est gérée dans le projet
   * back de Hypermed Online.
  **/
  private _edcProxyUrl: string;

  private basePath = environment.basePath + DOCUMENT_PATH;
  private lastAccessBasePath = environment.basePath + DMP_LAST_ACCESS_PATH;

  private _logCallSrv: LogFonctionnelCallerService;

  constructor(
    private _http: HttpClient,
    private _edcAuthSrv: EdcAuthService,
    private _loginService: LoginService,
    private _logSrv: LogFonctionnelService
  ) {
    super();
    if (!environment.edcProxyUrl) {
      throw new Error('EDC Proxy URL is not defined in environment configuration.');
    }
    this._edcProxyUrl = environment.edcProxyUrl;
  }

  /**
   * Point d'entrée unique et générique du serveur proxy HTTP vers l'Espace de Confiance
   */
  private async _callProxy<T>(targetPath: string, payload: any): Promise<T> {
    if (payload && typeof payload === 'object') {
      const practiceSetting = this._edcAuthSrv.practice_setting;
      if (!practiceSetting) {
        throw new Error("Le cadre d'exercice n'est pas renseigné. Vous devez le renseigner avant toutes choses.");
      }
      payload.PracticeLocation = {
        s_practiceSettings: practiceSetting
      };
      payload.s_correlationId = this._edcAuthSrv.s_correlationId || '';
      payload.s_dmpUrl = environment.dmpApiServerUrl || '';
    }

    const userName = this._edcAuthSrv.user_name;
    const userGiven = this._edcAuthSrv.user_given;
    const idNatPs = this._edcAuthSrv.id_nat_ps;

    if (!userName || !userGiven || !idNatPs) {
      throw new Error("Les informations d'authentification du médecin sont incomplètes. Veuillez vous reconnecter.");
    }

    const headers = new HttpHeaders()
      .set('X-Target-Path', targetPath)
      .set('X-User-Name', userName)
      .set('X-User-Given', userGiven)
      .set('X-IdNatPs', idNatPs);
    return this._http.post<T>(this._edcProxyUrl, payload, { headers }).toPromise();
  }

  /**
   * Génère le payload de base incluant le s_correlationId et les modes d'accès s'ils sont configurés
   */
  private _getBasePayload(patientIns?: string | DmpHlFindPatientResponseSIns | any): DmpRestGetDmpStatusBody {
    const payload: any = {
      s_correlationId: this._edcAuthSrv.s_correlationId
    };
    if (patientIns) {
      if (typeof patientIns === 'object') {
        const ins = patientIns.s_ins || '';
        const insType = patientIns.s_insType || '';
        payload.s_ins = `${ins}${insType}`;
      } else {
        payload.s_ins = patientIns;
      }
    }
    if (this.accessMode !== undefined && this.accessMode !== null) {
      payload.i_forceAccessMode = this.accessMode;
    }
    if (this._accessModeReason) {
      payload.s_accessModeReason = this._accessModeReason;
    }
    return payload;
  }

  public async hl_getdirectauthentificationdmpstatus(view: ViewContainerRef, patientIns: string): Promise<DmpHlGetDirectAuthentificationDmpStatus> {
    const payload = this._getBasePayload(patientIns);
    const res = await this._callProxy<any>('/getDmpStatus', payload);
    return mapRestToDmpStatus(res);
  }

  public async hl_updateuserdmpaccessauthorization(view: ViewContainerRef, authorizationParams: DmpHlUpdateUserAccessAuthorizationParams): Promise<void> {
    const payload: DmpRestUpdateDmpAccessAuthorizationBody = {
      ...this._getBasePayload(authorizationParams.s_ins),
      i_action: authorizationParams.i_action,
      i_setTreatingPhysician: authorizationParams.i_setTreatingPhysician
    };
    return this._callProxy<void>('/updateDmpAccessAuthorization', payload);
  }

  public async hl_setdmpaccessmode(view: ViewContainerRef, accessMode: DmpHlSetDmpAccessModeParams): Promise<void> {
    this.accessMode = accessMode.i_accessMode;
    this._accessModeReason = accessMode.s_reason;
  }

  public async hl_getwebpsdmpurls(view: ViewContainerRef, sIns: DmpHlFindPatientResponseSIns): Promise<DmpHlGetWebPsUrlsResponse> {
    const payload: DmpRestGetWebPsDmpUrlsBody = {
      ...this._getBasePayload(sIns),
      i_getPscUrls: 1
    };
    return this._callProxy<DmpHlGetWebPsUrlsResponse>('/getWebPsDmpUrls', payload);
  }

  public async hl_getInteropCodes(view: ViewContainerRef, sCodeIds: Array<DmpHlGetInteropCodesEnum>): Promise<DmpHlGetInteropCodesResponse> {
    const payload: DmpRestGetInteropCodesBody = { s_codeIds: sCodeIds };
    return this._callProxy<DmpHlGetInteropCodesResponse>('/getinteropcodes', payload);
  }

  public async hl_finddocuments(view: ViewContainerRef, ins: DmpHlFindPatientResponseSIns, searchParams: DmpHlFindDocumentsParams): Promise<Array<DmpHlFindDocumentsResponse>> {
    let categoriesObj: any = undefined;
    if (searchParams.Categories instanceof Map) {
      categoriesObj = {};
      let index = 0;
      searchParams.Categories.forEach((value, key) => {
        categoriesObj[`#${index}`] = key;
        index++;
      });
    } else if (searchParams.Categories && typeof searchParams.Categories === 'object') {
      const keys = Object.keys(searchParams.Categories);
      const isAlreadyMapped = keys.length > 0 && keys.every(k => k.startsWith('#'));
      if (isAlreadyMapped) {
        categoriesObj = searchParams.Categories;
      } else {
        categoriesObj = {};
        let index = 0;
        keys.forEach(key => {
          categoriesObj[`#${index}`] = key;
          index++;
        });
      }
    } else {
      categoriesObj = searchParams.Categories;
    }

    let formatsObj: any = undefined;
    if (searchParams.Formats instanceof Map) {
      formatsObj = {};
      let index = 0;
      searchParams.Formats.forEach((value, key) => {
        formatsObj[`#${index}`] = key;
        index++;
      });
    } else if (searchParams.Formats && typeof searchParams.Formats === 'object') {
      const keys = Object.keys(searchParams.Formats);
      const isAlreadyMapped = keys.length > 0 && keys.every(k => k.startsWith('#'));
      if (isAlreadyMapped) {
        formatsObj = searchParams.Formats;
      } else {
        formatsObj = {};
        let index = 0;
        keys.forEach(key => {
          formatsObj[`#${index}`] = key;
          index++;
        });
      }
    } else {
      formatsObj = searchParams.Formats;
    }

    const payload: DmpRestFindDocumentsBody = {
      ...this._getBasePayload(ins),
      Categories: categoriesObj,
      Formats: formatsObj,
      Practices: searchParams.Practices,
      s_creationDateBottom: searchParams.s_creationDateBottom,
      s_creationDateTop: searchParams.s_creationDateTop,
      s_serviceStartDateTop: searchParams.s_serviceStartDateTop,
      s_serviceStartDateBottom: searchParams.s_serviceStartDateBottom,
      s_serviceStopDateTop: searchParams.s_serviceStopDateTop,
      s_serviceStopDateBottom: searchParams.s_serviceStopDateBottom,
      s_submissionDateTop: searchParams.s_submissionDateTop,
      s_submissionDateBottom: searchParams.s_submissionDateBottom,
      i_status: searchParams.i_status,
      i_visibility: searchParams.i_visibility
    };
    const res = await this._callProxy<any>('/findDocuments', payload);
    return mapRestToFindDocuments(res);
  }

  public async hl_getdocumentcontent(view: ViewContainerRef, downloadParams: DmpHlGetDocumentContentParams): Promise<DmpHlGetDocumentContentResponse> {
    const payload: DmpRestGetDocumentContentBody = {
      ...this._getBasePayload(downloadParams.ins),
      s_documentUniqueId: downloadParams.uniqueId,
      s_documentUuid: downloadParams.uuid,
      i_getCdaHeaders: 1,
      i_getCdaContent: 1
    };
    return this._callProxy<DmpHlGetDocumentContentResponse>('/getDocumentContent', payload);
  }

  public async hl_deletedocument(view: ViewContainerRef, deleteParams: DmpHlDeleteDocumentParams, patientId?: string): Promise<void> {
    const payload: DmpRestDeleteDocumentBody = {
      ...this._getBasePayload(deleteParams.s_ins),
      s_documentUniqueId: deleteParams.s_uniqueId,
      s_healthcareSetting: deleteParams.s_healthcareSettings
    };
    try {
      await this._callProxy<void>('/deleteDocument', payload);
    } catch (e) {
      this._addLog(buildDeleteDocLog(ResultEnum.Error, deleteParams, patientId, e?.error || e?.message));
      throw e;
    }
    await this.deleteFromMyDocuments(deleteParams.s_uniqueId);
    this._addLog(buildDeleteDocLog(ResultEnum.Success, deleteParams, patientId));
  }

  public async hl_deleteArchivedDocument(view: ViewContainerRef, deleteParams: DmpHlDeleteDocumentParams, patientId?: string): Promise<void> {
    const payload: DmpRestDeleteDocumentBody = {
      ...this._getBasePayload(deleteParams.s_ins),
      s_documentUniqueId: deleteParams.s_uniqueId,
      s_healthcareSetting: deleteParams.s_healthcareSettings
    };
    try {
      await this._callProxy<void>('/deleteArchivedDocument', payload);
    } catch (e) {
      this._addLog(buildDeleteDocLog(ResultEnum.Error, deleteParams, patientId, e?.error || e?.message, true));
      throw e;
    }
    this._addLog(buildDeleteDocLog(ResultEnum.Success, deleteParams, patientId, undefined, true));
  }

  public async archiveDocumentByUniqueId(view: ViewContainerRef, archiveParams: DmpHlArchiveDocumentParams): Promise<void> {
    const sessionHealthcareSetting = this._getSessionHealthcareSetting();

    if (!sessionHealthcareSetting) {
      throw new Error(`Le code de secteur d'activité n'est pas connu de cette session.`);
    }

    const payload: DmpRestUpdateDocumentStatusBody = {
      ...this._getBasePayload(archiveParams.s_ins),
      s_documentUniqueId: archiveParams.s_documentUniqueId,
      s_documentUuid: archiveParams.s_documentUuid,
      s_healthcareSettings: archiveParams.s_healthcareSettings
    };
    return this._callProxy<void>('/updateDocumentStatus', payload);
  }

  public async updateVisibilityDocumentByUniqueId(view: ViewContainerRef, archiveParams: DmpHlVisibilityDocumentParams): Promise<any> {
    const payload: DmpRestUpdateDocumentVisibilityBody = {
      ...this._getBasePayload(archiveParams.s_ins),
      s_documentUniqueId: archiveParams.s_documentUniqueId,
      s_documentUuid: archiveParams.s_documentUuid,
      s_healthCareSettings: archiveParams.s_healthCareSettings,
      i_newVisibility: archiveParams.i_document_Visibility
    };
    return this._callProxy<any>('/updateDocumentVisibility', payload);
  }

  public async hl_getDocumentUuidByUniqueId(view: ViewContainerRef, documentInfos: { s_documentUniqueId: string, s_ins: DmpHlFindPatientResponseSIns }): Promise<string> {
    const payload: DmpRestGetDocumentUuidByUniqueIdBody = {
      ...this._getBasePayload(documentInfos.s_ins),
      s_documentUniqueId: documentInfos.s_documentUniqueId
    };
    const res = await this._callProxy<{ s_documentUuid: string }>('/getDocumentUuidByUniqueid', payload);
    return res ? res.s_documentUuid : '';
  }

  public async getCisCodes(view: ViewContainerRef, search?: string): Promise<Array<{ id: string, libelle: string }>> {
    const payload: DmpRestGetCisCodesBody = { s_name: search || '' };
    const res = await this._callProxy<any>('/getCisCodes', payload);
    return mapRestToCisCodes(res);
  }

  public async getAtcCodes(view: ViewContainerRef, search?: string): Promise<Array<{ id: string, libelle: string }>> {
    const payload: DmpRestGetAtcCodesBody = { s_nameFr: search || '' };
    const res = await this._callProxy<any>('/getAtcCodes', payload);
    return mapRestToAtcCodes(res);
  }

  public async getDmpParameters(view: ViewContainerRef): Promise<DmpParameters> {
    const payload: DmpRestGetDmpParametersBody = this._getBasePayload();
    const res = await this._callProxy<any>('/getDmpParameters', payload);
    return mapRestToDmpParameters(res);
  }

  public async getMajorityAge(view: ViewContainerRef): Promise<number> {
    const payload: DmpRestGetMajorityAgeBody = this._getBasePayload();
    const res = await this._callProxy<{ i_majorityAge: number }>('/getMajorityAge', payload);
    return res ? res.i_majorityAge : 18;
  }

  private async _addLog(obj: LogObject): Promise<void> {
    try {
      if (!this._logCallSrv) {
        this._logCallSrv = this._logSrv.getConfiguration(DomainEnum.Dmp);
      }

      this._logCallSrv.addLog(obj);
    } catch (e) {
      console.error(e);
    }
  }

  private _logSendDocError(err: any, dataSent: any, patientId?: string, isReplacement = false): void {
    this._addLog(buildSendDocErrorLog(err, dataSent, patientId, isReplacement));
  }

  private _logSendDocSuccess(rtd: any, dataSent?: any, patientId?: string, isReplacement = false): void {
    this._addLog(buildSendDocSuccessLog(rtd, dataSent, patientId, isReplacement));
  }

  private _getSessionHealthcareSetting(): string | undefined {
    try {
      const profil = this._loginService.getCurrentProfil();
      const profilId = this.resourceId || profil?.id;
      if (!profilId) {
        return undefined;
      }

      const existingMapStr = localStorage.getItem('edc_correlationId');
      if (existingMapStr) {
        const map = JSON.parse(existingMapStr);
        if (map && map[profilId]) {
          const entry = map[profilId];
          return entry.activity?.codeSecteurDactivite || undefined;
        }
      }
    } catch (e) {
      console.warn('Erreur lors de la récupération de sessionHealthcareSetting:', e);
    }
    return undefined;
  }

  /** Traduit un UserInfos (formulaire) vers la structure HP attendue dans Documents[].Authors[] */
  private _mapUserInfosToAuthor(user: UserInfos): DmpRestSendSubmissionSetAuthor {
    return {
      s_name: user.name,
      s_given: user.given,
      s_codeProfession: user.profession,
      s_professionOid: user.professionOid,
      s_codeSpecialty: user.speciality,
      s_internalId: user.internalId,
      i_internalIdType: user.internalIdType ? Number(user.internalIdType) : undefined
    };
  }

  /** Auteur par défaut à partir du professionnel de santé authentifié, utilisé quand aucun Author n'est renseigné dans le formulaire */
  private _getDefaultAuthor(): DmpRestSendSubmissionSetAuthor {
    return {
      s_name: this._edcAuthSrv.exercice?.nomDexercice || this._edcAuthSrv.user_name,
      s_given: this._edcAuthSrv.exercice?.prenomDexercice || this._edcAuthSrv.user_given,
      s_codeProfession: this._edcAuthSrv.exercice?.codeProfession,
      s_professionOid: DMP_PROFESSION_OID,
      s_codeSpecialty: this._edcAuthSrv.exercice?.codeSavoirFaire,
      s_internalId: this._edcAuthSrv.id_nat_ps,
      i_internalIdType: 3 // RPPS (DMPConnect.IdentifierType.IdentifierType_NationalIdentifierRpps)
    };
  }

  private _getAuthors(sendingParam: DmpHlsendDocumentsParams): Array<DmpRestSendSubmissionSetAuthor> {
    return sendingParam.Author && sendingParam.Author.length > 0
      ? sendingParam.Author.map(user => this._mapUserInfosToAuthor(user))
      : [this._getDefaultAuthor()];
  }

  /** Performer obligatoire côté API ("No Performer defined") : on retombe sur le PS authentifié si le formulaire n'en fournit pas */
  private _getPerformer(sendingParam: DmpHlsendDocumentsParams): DmpRestSendSubmissionSetPerformer {
    return {
      HP: sendingParam.Performer ? this._mapUserInfosToAuthor(sendingParam.Performer) : this._getDefaultAuthor(),
      i_role: DmpDocumentPerformerRoleEnum['Acteur']
    };
  }

  /**
   * Persistance Hypermed (table documents-dmp) d'un document envoyé au DMP.
   * Un échec est seulement tracé : il ne remet pas en cause l'envoi, déjà accepté par le DMP.
   * previousUniqueId renseigné (remplacement) → PUT sur l'ancien uniqueId, sinon POST.
   */
  private async _persistSentDocument(obj: DmpAddMyDocumentsParams, previousUniqueId?: string): Promise<void> {
    try {
      await this.addMyDocuments(obj, previousUniqueId);
    } catch (e) {
      console.error('Erreur lors de l\'enregistrement du document DMP dans Hypermed :', e);
    }
  }

  /**
   * Enregistre côté Hypermed (table documents-dmp) le uuid/uniqueId retourné par /sendsubmissionset,
   * pour reproduire le comportement de DmpWebsocketStrategyService.hl_senddocument.
   * L'uuid provient de la réponse de /sendsubmissionset (Uuids, renseigné grâce à i_retrieveDocumentUuid: 1).
   * Ne fait rien si uniqueId ou uuid est absent (même garde que côté websocket).
   */
  private async _registerSentDocument(
    sendingParam: DmpHlsendDocumentsParams,
    document: DmpHlsendDocumentsDocument,
    visibility: DmpVisibilityEnum,
    uniqueId: string | undefined,
    uuid: string | undefined,
    documentId: string | undefined
  ): Promise<void> {
    if (!uniqueId || !uuid) {
      return;
    }
    const obj: DmpAddMyDocumentsParams = {
      uuid,
      uniqueId,
      patientIns: sendingParam.s_ins.s_ins,
      rpps: this._edcAuthSrv.id_nat_ps,
      title: document.s_title,
      category: document.s_category,
      visibility,
      createdWithSecretmode: false
    };
    if (document.s_formatCode) {
      obj.formatCode = String(document.s_formatCode);
    } else if (document.i_format) {
      obj.format = document.i_format;
    }
    if (documentId) {
      obj.documentId = documentId;
    }
    if (document.s_description) {
      obj.comment = document.s_description;
    }
    await this._persistSentDocument(obj, document.s_uniqueId);
  }

  /**
   * Envoi d'un lot via /sendsubmissionset et journal fonctionnel du résultat (succès ou échec).
   * visibilities : valeurs numériques transmises à l'API, dans l'ordre de documents.
   * logData : document(s) journalisé(s), cf. buildSendDocSuccessLog / buildSendDocErrorLog.
   */
  private async _sendSubmissionSet(
    sendingParam: DmpHlsendDocumentsParams,
    documents: Array<DmpHlsendDocumentsDocument>,
    visibilities: Array<DmpVisibilityEnum>,
    logData: any,
    patientId?: string,
    isReplacement = false
  ): Promise<DmpRestSendSubmissionSetResponse> {
    const sessionHealthcareSetting = this._getSessionHealthcareSetting();
    const authors = this._getAuthors(sendingParam);
    const performer = this._getPerformer(sendingParam);
    const payload: DmpRestSendSubmissionSetBody = {
      ...this._getBasePayload(sendingParam.s_ins),
      s_healthcareSetting: sendingParam.s_healthcareSetting || sessionHealthcareSetting || undefined,
      i_retrieveDocumentUuid: 1,
      s_birthPlaceCode: sendingParam.s_birthPlaceCode,
      Documents: documents.map((doc, index) => ({
        s_title: doc.s_title,
        s_description: doc.s_description,
        s_category: doc.s_category,
        s_formatCode: doc.i_format ? undefined : doc.s_formatCode,
        i_format: doc.i_format || undefined,
        s_contentInBase64: doc.s_contentInBase64,
        i_visibility: visibilities[index],
        StructuredBody: (doc as any).StructuredBody || undefined,
        s_uniqueId: doc.s_uniqueId || undefined,
        Authors: authors,
        Performer: performer
      }))
    };
    let res: DmpRestSendSubmissionSetResponse;
    try {
      res = await this._callProxy<DmpRestSendSubmissionSetResponse>('/sendsubmissionset', payload);
    } catch (e) {
      this._logSendDocError(e?.error || e?.message, logData, patientId, isReplacement);
      throw e;
    }
    this._logSendDocSuccess(res, logData, patientId, isReplacement);
    return res;
  }

  /**
   * Retourne { s_uniqueId, s_uuid } (DmpRestSendDocumentResult), comme la réponse websocket.
   * Promise<any> pour rester compatible avec le contrat abstrait (Promise<void>).
   */
  public async hl_senddocument(view: ViewContainerRef, sendingParam: DmpHlsendDocumentsParams, document: DmpHlsendDocumentsDocument, documentId?: string, patientId?: string): Promise<any> {
    const visibility = (DmpVisibilityEnum[document.i_visibility] as unknown) as DmpVisibilityEnum;
    const res = await this._sendSubmissionSet(sendingParam, [document], [visibility], document, patientId, !!document.s_uniqueId);
    const sent: DmpRestSendDocumentResult = { s_uniqueId: res?.UniqueIds?.[0], s_uuid: res?.Uuids?.[0] };
    await this._registerSentDocument(sendingParam, document, visibility, sent.s_uniqueId, sent.s_uuid, documentId);
    return sent;
  }

  public async hl_senddocuments(view: ViewContainerRef, sendingParam: DmpHlsendDocumentsParams, documents: Array<DmpHlsendDocumentsDocument>, documentsId?: Array<string>, patientId?: string): Promise<void> {
    const visibilities = documents.map(doc => (DmpVisibilityEnum[doc.i_visibility] as unknown) as DmpVisibilityEnum);
    const isReplacement = documents.some(d => !!d.s_uniqueId);
    const res = await this._sendSubmissionSet(sendingParam, documents, visibilities, documents, patientId, isReplacement);
    for (let index = 0; index < documents.length; index++) {
      await this._registerSentDocument(sendingParam, documents[index], visibilities[index], res?.UniqueIds?.[index], res?.Uuids?.[index], documentsId?.[index]);
    }
  }

  // No-op / Simples résolutions pour la stratégie REST
  public async reinitializeConnector(view: ViewContainerRef): Promise<void> {
    return Promise.resolve();
  }

  public async setConfidentialityLevel(view: ViewContainerRef, enable: boolean): Promise<void> {
    return Promise.resolve();
  }

  public async getConfidentialityLevel(view: ViewContainerRef): Promise<boolean> {
    return Promise.resolve(false);
  }

  // ============================================================================
  // LOGIQUES DE HAUT NIVEAU RÉUTILISANT LES PRIMITIVES REST DMP
  // ============================================================================

  public async rechercherHistoriqueDeVaccination(view: ViewContainerRef, ins: DmpHlFindPatientResponseSIns): Promise<HistoriqueVaccination> {
    const categoriesMap = new Map<string, string>();
    categoriesMap.set('11369-6', '');

    const searchParams: DmpHlFindDocumentsParams = {
      Categories: categoriesMap,
      Formats: undefined,
      i_status: 1,
      i_visibility: undefined,
      Practices: undefined,
      s_creationDateBottom: undefined,
      s_creationDateTop: undefined,
      s_ins: ins,
      s_serviceStartDateBottom: undefined,
      s_serviceStartDateTop: undefined,
      s_serviceStopDateTop: undefined,
      s_serviceStopDateBottom: undefined,
      s_submissionDateBottom: undefined,
      s_submissionDateTop: undefined
    };

    const docs = await this.hl_finddocuments(view, ins, searchParams);
    if (!docs || docs.length === 0) {
      return undefined;
    }

    const downloadParams: DmpHlGetDocumentContentParams = {
      uniqueId: docs[0].s_uniqueId,
      uuid: docs[0].s_uuid,
      ins
    };

    // Même source que le websocket (hl_getDocumentFromCda) : Authors et StructuredBody issus du CDA
    const content = await this.hl_getdocumentcontent(view, downloadParams);
    if (!content?.s_cdaContentInBase64) {
      return undefined;
    }
    const payload: DmpRestGetDocumentFromCdaBody = { s_cdaContentInBase64: content.s_cdaContentInBase64 };
    return this._callProxy<HistoriqueVaccination>('/getDocumentFromCda', payload);
  }

  public async rechercherVaccinAValider(view: ViewContainerRef, ins: DmpHlFindPatientResponseSIns): Promise<Array<VaccinAValider>> {
    const rtd: Array<VaccinAValider> = [];
    const vaccsHisto = await this.rechercherHistoriqueDeVaccination(view, ins);
    if (vaccsHisto && vaccsHisto.Authors && vaccsHisto.Authors.length > 0
      && vaccsHisto.Authors.find(a => a.s_hpProfession === 'EXP_PATIENT')
      && vaccsHisto.StructuredBody?.Sections?.length > 0) {
      const patientIns = vaccsHisto.Authors.find(a => a.s_hpProfession === 'EXP_PATIENT').s_hpInternalId;
      if (patientIns && patientIns === ins.s_ins) {
        for (const sec of vaccsHisto.StructuredBody.Sections) {
          if (sec.Immunizations && sec.Immunizations.length > 0) {
            for (const imm of sec.Immunizations) {
              if (imm.Performer && imm.Performer.s_hpProfession === 'EXP_PATIENT') {
                rtd.push({
                  cis: imm.Consumable.s_cisCode,
                  date: imm.s_date,
                  consumable: {
                    name: imm.Consumable.s_name,
                    lotNumber: imm.Consumable.s_lotNumber,
                    cisCode: imm.Consumable.s_cisCode,
                    cisCodeDescription: imm.Consumable.s_cisCodeDescription,
                    atcCode: imm.Consumable.s_atcCode,
                    atcCodeDescription: imm.Consumable.s_atcCodeDescription
                  },
                  vaccinator: imm.Performer.s_hpName,
                  meta: imm as any
                });
              }
            }
          }
        }
      }
    }
    return rtd;
  }

  public async getVaccinationContent(view: ViewContainerRef, documentHandle: number): Promise<any> {
    return Promise.resolve(undefined);
  }

  public async ajouterNoteDeVaccin(view: ViewContainerRef, sendingParam: DmpHlsendDocumentsParams, consumable: VaccinationConsumable, author: VaccinationVaccinator, vaccinator: VaccinationVaccinator, dateInjection: Date, vaccinId?: UpdateNoteVaccinationId, patientId?: string): Promise<DmpVaccin> {
    const vaccinEntryId = (vaccinId?.vaccinEntryId || v4() as string).toUpperCase();
    const structuredBody = {
      Sections: [{
        i_type: 30,
        s_title: 'Note de vaccination',
        Immunizations: [{
          s_entryId: vaccinEntryId,
          s_date: moment(dateInjection).format('YYYYMMDD'),
          Comment: {
              s_text: " "
          },
          Consumable: {
            s_name: consumable.name,
            s_lotNumber: consumable.lotNumber,
            s_cisCode: consumable.cisCode,
            s_cisCodeDescription: consumable.cisCodeDescription,
            s_atcCode: consumable.atcCode,
            s_atcCodeDescription: consumable.atcCodeDescription
          },
          Author: author,
          Performer: vaccinator
        }]
      }]
    };

    const uniqueId = await this._sendVaccinationNote(sendingParam, structuredBody, consumable.comment, patientId, vaccinId?.vaccinUniqueId);

    return {
      entryId: vaccinEntryId,
      uniqueId
    };
  }

  public async deleteNoteDeVaccination(view: ViewContainerRef, deleteParams: DmpHlDeleteDocumentParams): Promise<void> {
    return this.hl_deleteArchivedDocument(view, deleteParams);
  }

  public async validerVaccin(view: ViewContainerRef, sendingParam: DmpHlsendDocumentsParams, vaccin: ValidationVaccination, patientId?: string): Promise<void> {
    const structuredBody = {
      Sections: [{
        i_type: 30,
        s_title: 'Note de vaccination',
        Immunizations: [{
          s_entryId: v4().toUpperCase(),
          s_date: moment(vaccin.date).format('YYYYMMDD'),
          Comment: {
              s_text: " "
          },
          Consumable: {
            s_name: vaccin.consumable.name,
            s_lotNumber: vaccin.consumable.lotNumber,
            s_cisCode: vaccin.consumable.cisCode,
            s_cisCodeDescription: vaccin.consumable.cisCodeDescription,
            s_atcCode: vaccin.consumable.atcCode,
            s_atcCodeDescription: vaccin.consumable.atcCodeDescription
          },
          Author: {
            s_hpName: vaccin.author.hpName,
            s_hpGiven: vaccin.author.hpGiven,
            s_hpProfession: vaccin.author.hpProfession,
            s_hpProfessionOid: vaccin.author.hpProfessionOid,
            s_hpInternalId: vaccin.author.hpInternalId,
            Institution: {
              s_id: vaccin.author.Institution.s_id,
              s_name: vaccin.author.Institution.s_name,
              s_classCode: vaccin.author.Institution.s_classCode
            }
          },
          Performer: {
            s_hpName: vaccin.vaccinator.hpName,
            s_hpGiven: vaccin.vaccinator.hpGiven,
            s_hpProfession: vaccin.vaccinator.hpProfession,
            s_hpProfessionOid: vaccin.vaccinator.hpProfessionOid,
            s_hpInternalId: vaccin.vaccinator.hpInternalId,
            Institution: {
              s_id: vaccin.vaccinator.Institution.s_id,
              s_name: vaccin.vaccinator.Institution.s_name,
              s_classCode: vaccin.vaccinator.Institution.s_classCode
            }
          }
        }]
      }]
    };

    await this._sendVaccinationNote(sendingParam, structuredBody, vaccin.consumable.comment, patientId);
  }

  /**
   * Envoi d'une note de vaccination puis persistance Hypermed unique, sur le modèle de la stratégie websocket :
   * journal avec les métadonnées de la note, pas de documentId, comment = commentaire du vaccin.
   * La visibilité est transmise à l'API sous sa valeur numérique (pas de conversion libellé → valeur comme dans hl_senddocument).
   * Retourne le uniqueId attribué par le DMP.
   */
  private async _sendVaccinationNote(
    sendingParam: DmpHlsendDocumentsParams,
    structuredBody: any,
    comment: string | undefined,
    patientId?: string,
    previousUniqueId?: string
  ): Promise<string | undefined> {
    const visibility = DmpVisibilityEnum['Le document est visible de tous'];
    const immunization = structuredBody.Sections[0].Immunizations[0];
    const docToSend: any = {
      s_title: 'Note de vaccination',
      s_description: 'Note de vaccination',
      s_category: '87273-9',
      s_formatCode: 'urn:asip:ci-sis:vac-note:2021',
      i_format: 61,
      i_visibility: visibility,
      StructuredBody: structuredBody,
      s_uniqueId: previousUniqueId || undefined
    };
    const documentMeta = {
      s_title: 'Note de vaccination',
      documentId: undefined,
      s_uniqueId: undefined,
      s_category: '87273-9',
      s_classCode: 'Note de Vaccination',
      s_formatCode: 'urn:asip:ci-sis:vac-note:2021',
      i_format: 61,
      s_description: 'Note de Vaccination',
      i_visibility: visibility,
      authors: { auteur: immunization.Author, vaccinateur: immunization.Performer },
      dateCreation: immunization.s_date,
      vaccinInformation: structuredBody
    };

    const res = await this._sendSubmissionSet(sendingParam, [docToSend], [visibility], documentMeta, patientId);
    const uniqueId = res?.UniqueIds?.[0];
    if (uniqueId) {
      const obj: DmpAddMyDocumentsParams = {
        uuid: res?.Uuids?.[0] || '',
        uniqueId,
        patientIns: sendingParam.s_ins.s_ins,
        rpps: this._edcAuthSrv.id_nat_ps,
        title: 'Note de vaccination',
        formatCode: 'urn:asip:ci-sis:vac-note:2021',
        category: '87273-9',
        visibility,
        createdWithSecretmode: false
      };
      if (comment) {
        obj.comment = comment;
      }
      await this._persistSentDocument(obj, previousUniqueId);
    }
    return uniqueId;
  }

  // ============================================================================
  // REQUÊTES DIRECTES AU BACKEND HYPERMED LOCAL (IDENTIQUE AUX WEBSOCKETS)
  // ============================================================================

  public async getMyDocuments(myDocsParams: DmpGetMyDocumentsParams): Promise<Array<DmpGetMyDocumentsResult>> {
    return new Promise<Array<DmpGetMyDocumentsResult>>(async (resolve, reject) => {
      try {
        let httpParams = new HttpParams();
        httpParams = httpParams.append('patientIns', myDocsParams.patientIns);
        httpParams = httpParams.append('rpps', encodeURIComponent(myDocsParams.rpps));

        const res = await this._http.get<Array<DmpGetMyDocumentsResult>>(`${this.basePath}`, { params: httpParams }).toPromise();
        resolve(res);
      } catch (e) {
        reject(e);
      }
    });
  }

  public async deleteFromMyDocuments(uniqueId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http.delete<void>(`${this.basePath}/${uniqueId}`).toPromise();
        resolve();
      } catch (e) {
        reject(e);
      }
    });
  }

  public async addMyDocuments(myDocsParams: DmpAddMyDocumentsParams, uniqueId?: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        if (uniqueId) {
          await this._http.put<Array<DmpGetMyDocumentsResult>>(`${this.basePath}/${uniqueId}`, myDocsParams).toPromise();
        } else {
          await this._http.post<Array<DmpGetMyDocumentsResult>>(`${this.basePath}`, myDocsParams).toPromise();
        }
        resolve();
      } catch (e) {
        reject(e);
      }
    });
  }

  public async updateMyDocumentsDocumentId(uniqueId: string, documentId: string): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        await this._http.put<Array<DmpGetMyDocumentsResult>>(`${this.basePath}/${uniqueId}/documentId`, { documentId }).toPromise();
        resolve();
      } catch (e) {
        reject(e);
      }
    });
  }

  public async searchDMPDocumentByDocumentId(list: Array<string>): Promise<Array<{ documentDmp: DmpGetMyDocumentsResult, documentId: string }>> {
    const headers = new HttpHeaders().set('Content-Type', 'application/json; charset=utf-8');
    const res = await this._http.post<Array<{ documentDmp: DmpGetMyDocumentsResult, documentId: string }>>(`${this.basePath}/documentId/search`, JSON.stringify(list), { headers: headers }).toPromise();
    return res;
  }

  public async getLastAccessDate(lastAccessParams: DmpGetLastAccessDateParams): Promise<Date | undefined> {
    return new Promise<Date | undefined>(async (resolve, reject) => {
      try {
        if (lastAccessParams.patientIns && lastAccessParams.rpps) {
          const date = await this._http.get<string>(`${this.lastAccessBasePath}/${lastAccessParams.patientIns}/${lastAccessParams.rpps}`).toPromise();
          resolve(date ? moment(date).toDate() : undefined);
        } else {
          resolve(undefined);
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  public async setLastAccessDate(lastAccessParams: DmpGetLastAccessDateParams): Promise<void> {
    return new Promise<void>(async (resolve, reject) => {
      try {
        if (lastAccessParams.patientIns && lastAccessParams.rpps) {
          await this._http.put(`${this.lastAccessBasePath}/${lastAccessParams.patientIns}/${lastAccessParams.rpps}`, undefined).toPromise();
          resolve();
        } else {
          resolve();
        }
      } catch (e) {
        reject(e);
      }
    });
  }
}
